// The one file of the client that reads `navigator.gpu`.
//
// Every other module takes the probe as an argument, so the whole selection
// matrix is tested in jsdom, which has no WebGPU at all (`gfx/select.ts`).
// `gfx/seam.test.ts` pins that the token appears here and in
// `gpu/context.ts` and nowhere else.
//
// The probe asks for a HIGH-PERFORMANCE adapter, because the client draws a
// full-screen picture at 60 Hz and the integrated part of a two-GPU machine
// is not what the owner tests on. It asks for `timestamp-query` where the
// adapter carries it, so `gpuMs` has a source; a device without it still
// draws and reports no GPU column.
//
// The context of `gpu/context.ts` calls it a second time after a device
// loss, which is why the whole probe is one function and not inlined.

/** A live WebGPU device, and what the stats line says about it. */
export interface GpuBundle {
  device: GPUDevice;
  /** The adapter's own description, for the stats line. */
  info: string;
  /** Did the device get the `timestamp-query` feature? */
  timestamps: boolean;
}

/**
 * The browser's WebGPU entry, or undefined where the browser has none.
 *
 * `createEngine` reads it through this call rather than by itself, so the
 * token stays in this file and `gfx/seam.test.ts` can pin that.
 */
export function navigatorGpu(): GPU | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator.gpu;
}

/**
 * Asks the browser for a device, or answers null.
 *
 * It never throws: a browser that has no WebGPU entry, an adapter that
 * refuses a device and a driver that faults all give the same null, and the
 * client falls back to WebGL2.
 */
export async function requestGpu(gpu: GPU | undefined): Promise<GpuBundle | null> {
  if (!gpu) return null;
  try {
    const adapter = await gpu.requestAdapter({ powerPreference: 'high-performance' });
    if (!adapter) return null;
    const timestamps = !!adapter.features?.has('timestamp-query');
    const device = await adapter.requestDevice(
      timestamps ? { requiredFeatures: ['timestamp-query'] } : {});
    if (!device) return null;
    return { device, info: adapterInfo(adapter), timestamps };
  } catch {
    // A driver that faults, a device the browser refuses, a policy that
    // blocks WebGPU: all of them are one answer, and the client falls back.
    return null;
  }
}

/**
 * The adapter's own description, as the stats line prints it after `gpu:`.
 *
 * `adapter.info` is absent on an older browser, and each of its four parts
 * may be an empty string, so every read is guarded and an empty part is
 * dropped rather than printed as a gap.
 */
function adapterInfo(adapter: GPUAdapter): string {
  const info = adapter.info;
  const parts = [
    info?.vendor, info?.architecture, info?.device, info?.description,
  ].filter((p): p is string => !!p && p.length > 0);
  return parts.length > 0 ? parts.join(' ') : 'webgpu';
}
